export type PairingType =
  | 'MF'
  | 'MM'
  | 'FF'
  | 'MMF'
  | 'MFM'
  | 'Reverse Harem / Why Choose'
  | 'Polyamorous'

export interface RomanceTrope {
  name: string
  isHighImpact: boolean
  isFlagged: boolean
  specificityScore: number // Higher is more specific
}

export interface CharacterPairing {
  name: string
  isFlagged: boolean
}

export interface ContentWarning {
  name: string
  definition: string
}

export type DarknessLevel = 'Light' | 'Medium' | 'Dark' | 'Pitch Black'

export const PAIRING_TYPES: PairingType[] = [
  'MF',
  'MM',
  'FF',
  'MMF',
  'MFM',
  'Reverse Harem / Why Choose',
  'Polyamorous',
]

export const ROMANCE_TROPES: RomanceTrope[] = [
  {
    name: 'Enemies to Lovers',
    isHighImpact: true,
    isFlagged: false,
    specificityScore: 5,
  },
  {
    name: 'Friends to Lovers',
    isHighImpact: false,
    isFlagged: false,
    specificityScore: 3,
  },
  {
    name: 'Forbidden Love',
    isHighImpact: true,
    isFlagged: true,
    specificityScore: 6,
  },
  {
    name: 'Fake Dating',
    isHighImpact: true,
    isFlagged: false,
    specificityScore: 5,
  },
  {
    name: 'Second Chance',
    isHighImpact: false,
    isFlagged: false,
    specificityScore: 4,
  },
  {
    name: 'Captor/Captive',
    isHighImpact: true,
    isFlagged: true,
    specificityScore: 9,
  },
  {
    name: 'Marriage of Convenience',
    isHighImpact: true,
    isFlagged: false,
    specificityScore: 6,
  },
  {
    name: 'Forced Proximity',
    isHighImpact: false,
    isFlagged: false,
    specificityScore: 4,
  },
  {
    name: 'Secret Relationship',
    isHighImpact: false,
    isFlagged: false,
    specificityScore: 5,
  },
  {
    name: 'Age Gap',
    isHighImpact: false,
    isFlagged: true,
    specificityScore: 6,
  },
  {
    name: 'Mafia Romance',
    isHighImpact: false,
    isFlagged: true,
    specificityScore: 8,
  },
  {
    name: 'Slow Burn',
    isHighImpact: false,
    isFlagged: false,
    specificityScore: 2,
  },
  {
    name: 'Instalove',
    isHighImpact: false,
    isFlagged: false,
    specificityScore: 2,
  },
  {
    name: 'Stuck Together',
    isHighImpact: false,
    isFlagged: false,
    specificityScore: 3,
  },
  {
    name: 'Snowed In',
    isHighImpact: false,
    isFlagged: false,
    specificityScore: 4,
  },
  {
    name: 'Hidden Identity',
    isHighImpact: false,
    isFlagged: false,
    specificityScore: 5,
  },
  {
    name: 'Bodyguard Romance',
    isHighImpact: false,
    isFlagged: false,
    specificityScore: 7,
  },
  {
    name: 'Arranged Marriage',
    isHighImpact: false,
    isFlagged: false,
    specificityScore: 6,
  },
]

export const CHARACTER_PAIRINGS: CharacterPairing[] = [
  { name: 'Monster/Human', isFlagged: false },
  { name: 'Billionaire/Commoner', isFlagged: false },
  { name: 'Royal/Commoner', isFlagged: false },
  { name: 'Boss/Employee', isFlagged: true },
  { name: 'Teacher/Student', isFlagged: true },
  { name: 'Alien/Human', isFlagged: false },
  { name: 'Vampire/Human', isFlagged: false },
  { name: 'Shifter/Human', isFlagged: false },
]

export const CONTENT_WARNINGS: ContentWarning[] = [
  {
    name: 'Dubious Consent',
    definition: 'Consent is unclear, pressured, or impaired.',
  },
  { name: 'Non-Consensual Content', definition: 'Explicit lack of consent.' },
  {
    name: 'Kidnapping',
    definition: 'Character is taken or held against their will.',
  },
  {
    name: 'Abuse',
    definition: 'Physical, emotional, or psychological abuse depicted.',
  },
  {
    name: 'Stalking',
    definition: 'Obsessive surveillance or pursuit behavior.',
  },
  {
    name: 'Coercion',
    definition: 'Consent given under pressure, manipulation, or threat.',
  },
  { name: 'Violence', definition: 'Graphic depictions of physical violence.' },
  { name: 'Self-Harm', definition: 'Depictions of self-inflicted harm.' },
  { name: 'Substance Abuse', definition: 'Drug or alcohol abuse depicted.' },
  {
    name: 'Death of Loved One',
    definition: 'Significant character death as plot element.',
  },
]

export const DARKNESS_LEVELS: { level: DarknessLevel; description: string }[] =
  [
    {
      level: 'Light',
      description: 'Light tension — mild conflict, no graphic content',
    },
    {
      level: 'Medium',
      description:
        'Moderate darkness — morally grey characters, some intensity',
    },
    {
      level: 'Dark',
      description: 'Dark themes — violence, trauma, or heavy emotional content',
    },
    {
      level: 'Pitch Black',
      description: 'Extreme darkness — no limits, disturbing themes',
    },
  ]

export const OVERLAP_CLUSTERS: Record<string, string[]> = {
  Forbidden: ['Forbidden Love', 'Secret Relationship', 'Hidden Identity'],
  Power: [
    'Boss/Employee',
    'Teacher/Student',
    'Royal/Commoner',
    'Billionaire/Commoner',
  ],
  Captivity: ['Captor/Captive', 'Kidnapping', 'Forced Proximity'],
  Proximity: ['Forced Proximity', 'Stuck Together', 'Snowed In'],
}

export interface DirectMapping {
  tag: string
  dynamics: string[]
  aftertide: boolean | 'Compound'
}

export const DIRECT_MAPPINGS: DirectMapping[] = [
  {
    tag: 'Captor/Captive',
    dynamics: ['Captive Dynamic', 'Power Imbalance'],
    aftertide: true,
  },
  {
    tag: 'Teacher/Student',
    dynamics: ['Authority Imbalance', 'Power Imbalance'],
    aftertide: true,
  },
  {
    tag: 'Boss/Employee',
    dynamics: ['Authority Imbalance', 'Power Imbalance'],
    aftertide: false,
  },
  {
    tag: 'Billionaire/Commoner',
    dynamics: ['Financial Imbalance', 'Power Imbalance'],
    aftertide: false,
  },
  {
    tag: 'Royal/Commoner',
    dynamics: ['Status Imbalance', 'Power Imbalance'],
    aftertide: false,
  },
  {
    tag: 'Mafia Romance',
    dynamics: ['Possessive Dynamic', 'Power Imbalance'],
    aftertide: 'Compound',
  },
  { tag: 'Dubious Consent', dynamics: ['Consent Complexity'], aftertide: true },
  {
    tag: 'Non-Consensual Content',
    dynamics: ['Consent Violation'],
    aftertide: true,
  },
  { tag: 'Kidnapping', dynamics: ['Captive Dynamic'], aftertide: true },
  { tag: 'Stalking', dynamics: ['Obsessive Dynamic'], aftertide: true },
  { tag: 'Age Gap', dynamics: ['Power Imbalance'], aftertide: false },
]

export interface CompoundRule {
  ifHasAll?: string[]
  ifHasAnyWarning?: boolean
  ifDarkness?: DarknessLevel[]
  thenDynamics?: string[]
  thenAftertide?: boolean
}

export const COMPOUND_RULES: CompoundRule[] = [
  {
    ifHasAll: ['Mafia Romance'],
    ifDarkness: ['Dark', 'Pitch Black'],
    thenAftertide: true,
  },
  {
    ifHasAll: ['Mafia Romance'],
    ifHasAnyWarning: true,
    thenAftertide: true,
  },
  {
    ifHasAll: ['Fake Dating', 'Boss/Employee'],
    thenDynamics: ['Authority Imbalance'],
  },
  {
    ifHasAll: ['Enemies to Lovers', 'Captor/Captive'],
    thenDynamics: ['High Conflict Dynamic'],
  },
  {
    ifHasAll: ['Age Gap', 'Boss/Employee'],
    thenDynamics: ['Compounded Power Imbalance'],
    thenAftertide: true,
  },
  {
    ifHasAll: ['Age Gap', 'Teacher/Student'],
    thenDynamics: ['Compounded Power Imbalance'],
    thenAftertide: true,
  },
]
